/* eslint-disable camelcase */
import React, { useEffect, ReactChild, ReactElement } from "react";
import { useInView, IntersectionOptions } from "react-intersection-observer";
import {
    JobSearchData,
    JobSearchCategory,
    JobSearchLevel,
    JobSearchLocation
} from "@themuse/design-system";

import {
    SingleJob, SingleCompany, JobLocation, JobLevel, JobCategory
} from "interfaces/api/apiInterfaces";
import { getOptimizeData, isBrowser } from "./browser";
import { formatLog } from "./logging";

interface TrackedElementProps {
    children: ReactChild;
    eventName: string;
    eventProps?: Object;
    enabled?: boolean;
    callback?: () => void;
}

interface IOskar {
    setGlobalUserProperties: (properties: any) => void;
    setGlobalEventProperties: (properties: any) => void;
    capture: (eventType: string, eventProperties: any) => void;
    setEnvironment: (environment: string, url?: string) => void;
    getOskarID: (callback: (oskarID: string) => void) => void;
    getSessionID: (callback: (sessionID: string) => void) => void;
}

interface OskarWindow extends Window {
    OskarTS?: IOskar;
}

declare const window: OskarWindow;

const appVersion = process.env.VERSION?.split(".").slice(0, 2).join(".");

interface BaseProps {
    app_version_id: string;
    page_referrer: string;
    query_string: string;
    page: string;
    path: string;
    experiments: any[];
    tm_source: string;
}

export interface JobTrackingProps {
    company_id: number;
    company_name: string;
    company_short_name: string;
    job_applied: boolean;
    job_categories: string[];
    job_favorited: boolean;
    job_id: number;
    job_level: string[];
    job_locations: string[];
    job_name: string;
    job_short_name: string;
    job_state: string;
    job_type: string;
    job_ats_name?: string;
    placement_position?: string | null;
}

export interface LinkClickedTrackingProps {
    click_origin?: string;
    new_tab?: boolean;
    link?: string;
}

const hasOskar = () => typeof window.OskarTS !== "undefined";

/**
 * Generate the base properties for any event. We added url data, experiment
 * data. And a few other things as well.
 *
 * @export
 * @returns {BaseProps}
 */
const genBaseProps = (): BaseProps => ({
    app_version_id: appVersion,
    page_referrer: document?.referrer ?? "",
    query_string: window?.document?.location?.search ?? "",
    page: window?.document?.location?.pathname ?? "",
    path: window?.document?.location?.pathname ?? "",
    experiments: [],
    tm_source: ""
});

/**
 * Tracks an event name and event properties. It add some base
 * properties including page data and referrer data to the event being tracked
 *
 * @param {string} eventName
 * @param {*} [eventProps={}]
 */
const museTrack = (eventName: string, eventProps: Object = {}) => {
    try {
        if (isBrowser() && hasOskar()) {
            const baseProps = genBaseProps();

            const optimizeData = getOptimizeData();
            if (optimizeData) {
                baseProps.experiments = [...baseProps.experiments, ...optimizeData];
            }

            window?.OskarTS?.capture(eventName, { ...eventProps, ...baseProps });
        }
    } catch (error) {
        const msg = `${process.env.APP_NAME} was unable to capture ${eventName}.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

/**
 * Tracks a user
 *
 * @param {*} [userProps={}]
 */
const userTrack = (userProps: Object = {}) => {
    try {
        if (isBrowser() && hasOskar()) {
            window.OskarTS.setGlobalUserProperties({ ...userProps });
        }
    } catch (error) {
        const msg = "Job Renderer was unable to populate the Global User Properties.";
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

/**
 * TrackedElement: Wraps an element in an intersection observer with tracking props.
 */
const TrackedElement = (props: TrackedElementProps & IntersectionOptions) => {
    const {
        children, eventName, eventProps = {}, enabled = true, callback, ...restProps
    } = props;

    const intersectionProps: IntersectionOptions = {
        triggerOnce: true,
        rootMargin: "0px",
        ...restProps
    };
    const { ref, inView } = useInView(intersectionProps);

    useEffect(() => {
        if (inView && enabled) {
            museTrack(eventName, eventProps);
            if (callback) {
                callback();
            }
        }
    }, [inView]);

    return (
        <>
            {React.Children.map(children, (child: ReactElement) => React.cloneElement(child, { ref }))}
        </>
    );
};

/**
 * Find a specific type of ancestor of this element.
 */
const findParentElement = (el: Element, elementTypes: string[]): Element | null => {
    if (elementTypes.includes(el.tagName)) {
        return el;
    }
    if (el.parentElement && el.parentElement !== document.body) {
        return findParentElement(el.parentElement, elementTypes);
    }
    return null;
};

/**
 * Fire an event only if the element or one of its parents is an "A".
 */
const getLinkTrackingProps = (e: MouseEvent | React.MouseEvent<HTMLElement, HTMLAnchorElement>) => {
    const el: Element | HTMLAnchorElement | null = findParentElement(e.target as Element, ["A"]);
    if (!el) {
        return;
    }
    const href = (el as HTMLAnchorElement).getAttribute("href");
    const placement_position = (el as HTMLAnchorElement).getAttribute("data-pos") || "";
    const trackingProps = {
        experiments: [],
        link_href: href,
        link_name: el.textContent,
        page_referrer: document.referrer,
        path: window.location.pathname,
        placement_position,
        query_string: window.location.search,
        right_click: e.type === "contextmenu",
        status_code: 200,
    };
    museTrack("Global Link Clicked", trackingProps);
};

/**
 * Will trigger Header Link Clicked event if event target is a nav link.
 */
const trackHeaderNavLinks = (e: MouseEvent | React.MouseEvent<HTMLElement, HTMLAnchorElement>) => {
    const el: Element | HTMLAnchorElement | null = findParentElement(e.target as Element, ["A"]);
    if (!el || !el.hasAttribute("data-header-link")) {
        return;
    }
    const href = el.getAttribute("href");
    const trackingProps = {
        ...genBaseProps(),
        header_link: el.getAttribute("data-header-link"),
        link_href: href,
        link_name: el.textContent,
        placement_position: "",
        right_click: e.type === "contextmenu",
        text: el.textContent,
    };
    museTrack("Header Link Clicked", trackingProps);
};

/**
 * getJobTrackingProps: Generate the tracking props for a single job.
 */
const getJobTrackingProps = (job: SingleJob, company: SingleCompany): JobTrackingProps => ({
    company_id: job?.company_id,
    company_name: company?.name,
    company_short_name: company?.short_name,
    job_applied: false,
    job_categories: job?.categories?.map((category: JobCategory) => category.value),
    job_favorited: false,
    job_id: job?.id,
    job_level: job?.levels?.map((level: JobLevel) => level.value),
    job_locations: job?.locations?.map((location: JobLocation) => location.value),
    job_name: job?.name,
    job_short_name: job?.short_name,
    job_state: job?.state,
    job_type: job?.type,
    job_ats_name: job?.ats_info?.provider,
    placement_position: null,
});

const getJobSearchHitTrackingProps = (jobSearchHit: JobSearchData): JobTrackingProps => ({
    company_id: jobSearchHit?.company?.legacy_id,
    company_name: jobSearchHit?.company?.name,
    company_short_name: jobSearchHit?.company?.short_name,
    job_applied: false,
    job_categories: jobSearchHit?.categories?.map((category: JobSearchCategory) => category.name),
    job_favorited: false,
    job_id: jobSearchHit?.legacy_id,
    job_level: jobSearchHit?.levels?.map((level: JobSearchLevel) => level.name),
    job_locations: jobSearchHit?.locations?.map((location: JobSearchLocation) => location.address),
    job_name: jobSearchHit?.title,
    job_short_name: jobSearchHit?.short_title,
    job_state: "live",
    job_type: "external"
});

export {
    TrackedElement,
    appVersion,
    findParentElement,
    genBaseProps,
    getJobTrackingProps,
    getJobSearchHitTrackingProps,
    getLinkTrackingProps,
    hasOskar,
    museTrack,
    userTrack,
    trackHeaderNavLinks
};

export type {
    BaseProps,
    IOskar,
    OskarWindow,
    TrackedElementProps
};
